import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Calendar, Search } from "lucide-react";
import api from "@/api";
import { useLanguage } from "@/context/LanguageContext";
import { AutocompleteInput } from "@/components/AutocompleteInput";
import { TripCard, ParcelCard } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const { t, lang } = useLanguage();
  const [results, setResults] = useState(null);
  const [form, setForm] = useState({
    origin: params.get("origin") || "",
    destination: params.get("destination") || "",
    date: params.get("date") || "",
    type: params.get("type") || "both",
  });

  useEffect(() => {
    setResults(null);
    const q = { origin: params.get("origin") || "", destination: params.get("destination") || "", date: params.get("date") || "", lang };
    const type = params.get("type") || "both";
    const reqs = [];
    if (type !== "parcels") reqs.push(api.get("/trips", { params: q }).then((r) => r.data.map((x) => ({ ...x, _kind: "trip" }))));
    if (type !== "rides") reqs.push(api.get("/parcels", { params: q }).then((r) => r.data.map((x) => ({ ...x, _kind: "parcel" }))));
    Promise.all(reqs)
      .then((lists) => setResults(lists.flat().sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")))))
      .catch(() => setResults([]));
  }, [params, lang]);

  useEffect(() => {
    if (!form.origin && !form.destination && !form.date && (params.get("origin") || params.get("destination") || params.get("date"))) {
      const p = new URLSearchParams();
      p.set("type", form.type);
      setParams(p);
    }
  }, [form]);

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (form.origin) p.set("origin", form.origin);
    if (form.destination) p.set("destination", form.destination);
    if (form.date) p.set("date", form.date);
    p.set("type", form.type);
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="search-page">
      <h1 className="text-3xl md:text-4xl font-bold text-primary mb-8">{t("search.title")}</h1>
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(15,41,66,0.10)] p-4 sm:p-5 mb-10 grid gap-3 md:grid-cols-[1fr_1fr_auto_auto_auto] items-center" data-testid="search-filter-form">
        <AutocompleteInput
          testid="search-origin-input"
          value={form.origin}
          onChange={(v) => setForm({ ...form, origin: v })}
          placeholder={t("hero.originPlaceholder")}
          inputClassName="pl-10 h-12 rounded-xl"
          icon={<MapPin size={16} className="absolute left-3.5 top-6 -translate-y-1/2 text-slate-400 z-10 pointer-events-none" />}
        />
        <AutocompleteInput
          testid="search-destination-input"
          value={form.destination}
          onChange={(v) => setForm({ ...form, destination: v })}
          placeholder={t("hero.destinationPlaceholder")}
          inputClassName="pl-10 h-12 rounded-xl"
          icon={<MapPin size={16} className="absolute left-3.5 top-6 -translate-y-1/2 text-accent z-10 pointer-events-none" />}
        />
        <div className="relative">
          <Calendar size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input data-testid="search-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="pl-10 h-12 rounded-xl" aria-label={t("hero.date")} />
        </div>
        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
          <SelectTrigger data-testid="search-type-select" className="h-12 rounded-xl md:w-44"><SelectValue placeholder={t("search.type")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="rides" data-testid="search-type-rides">{t("search.rides")}</SelectItem>
            <SelectItem value="parcels" data-testid="search-type-parcels">{t("search.parcels")}</SelectItem>
            <SelectItem value="both" data-testid="search-type-both">{t("search.both")}</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="search-submit-button" type="submit" className="h-12 px-8 rounded-xl bg-accent hover:bg-accent/90 text-white font-semibold">
          <Search size={16} className="mr-2" /> {t("search.apply")}
        </Button>
      </form>
      {results === null ? (
        <p className="text-slate-500" data-testid="search-loading">{t("common.loading")}</p>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-6" data-testid="search-count">{results.length} {t("search.results")}</p>
          {results.length === 0 ? (
            <div className="bg-white border border-slate-100 rounded-2xl p-12 text-center text-slate-500" data-testid="search-empty">{t("search.noResults")}</div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="search-results">
              {results.map((item) => item._kind === "parcel" ? <ParcelCard key={item.id} parcel={item} /> : <TripCard key={item.id} trip={item} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
